/**
 * Device presets - define the canvas dimensions for each (device, orientation)
 * combo. The canvas always renders at these exact pixel dimensions, then
 * scale-to-fit math in the page wrapper rescales it to fill the viewport
 * while preserving aspect ratio (so the design at 1180x820 looks identical
 * on a 13" laptop screen and on a 1180x820 iPad).
 *
 * UNITS ARE CSS POINTS, NOT PHYSICAL PIXELS. This is the whole ballgame and
 * it is why this comment exists.
 *
 * The iPhone 12 Pro Max row used to read 1284x2778, which is the spec-sheet
 * number - the hardware pixel count at 3x density. In CSS the phone is
 * 428x926. A project created on that preset therefore had a canvas THREE
 * TIMES too large in every direction, and `min(1, availW/w, availH/h)` drove
 * the shell scale to 0.27 on an iPad. The operator hit exactly that on
 * 2026-07-31: the whole rig rendered at 27%, illegibly small, and because
 * the chrome bar scales with the shell, the menu needed to undo it was also
 * at 27%. They had to physically rotate the device to read a menu.
 *
 * If a preset is ever added from a spec sheet again: divide by the device's
 * scale factor first. iPad rows have always been correct (820x1180 CSS).
 */

export type DeviceId = 'ipad' | 'ipad-10-2' | 'iphone-12-pro-max';
export type Orientation = 'landscape' | 'portrait';

export interface DevicePreset {
  id: DeviceId;
  label: string;
  /** Width in the device's portrait orientation, in CSS points. Landscape is
   *  height/width swapped. */
  portraitW: number;
  portraitH: number;
}

/**
 * ⚠️ "iPad" IS NOT ONE SIZE, and calling the 1180x820 row just "iPad" hid that
 * for months. 820x1180 is an iPad Air / Pro 11-inch. The operator gigs on an
 * iPad 8th gen (10.2-inch), which is 810x1080 - a 4:3 panel against the Air's
 * ~16:11. Different ASPECT, so scale-to-fit cannot cover it: the shell
 * letterboxes and no styling reaches the screen edge.
 *
 * MEASURED off a screenshot from the device, 2026-09-22: screen 2160x1620 at
 * @2x = 1080x810 points, shell drawn at 91.5%, leaving a 46-point dead band
 * below the app and 10 above. That band is what the operator kept pointing at.
 * The label is now explicit on both rows so the next person does not have to
 * rediscover that "iPad" meant one specific iPad.
 */
export const DEVICES: DevicePreset[] = [
  { id: 'ipad',              label: 'iPad Air / Pro 11-inch', portraitW: 820, portraitH: 1180 },
  { id: 'ipad-10-2',         label: 'iPad 10.2-inch',         portraitW: 810, portraitH: 1080 },
  { id: 'iphone-12-pro-max', label: 'iPhone 12 Pro Max',      portraitW: 428, portraitH: 926 },
];

/** The pre-fix iPhone dimensions, kept ONLY so the v3 -> v4 storage
 *  migration can recognise coordinates authored in the wrong space and
 *  rescale them. Do not use for anything else. */
export const LEGACY_IPHONE_PORTRAIT = { w: 1284, h: 2778 };

/** Factor to convert a coordinate authored against LEGACY_IPHONE_PORTRAIT
 *  into the corrected CSS-point space. Exactly 1/3 (the device's scale
 *  factor), which is why the correction is lossless for round numbers. */
export const LEGACY_IPHONE_FIX_FACTOR = 428 / 1284;

export function canvasSize(device: DeviceId, orientation: Orientation): { w: number; h: number } {
  const preset = DEVICES.find((d) => d.id === device);
  if (!preset) return { w: 1180, h: 820 }; // fallback: iPad landscape
  if (orientation === 'landscape') return { w: preset.portraitH, h: preset.portraitW };
  return { w: preset.portraitW, h: preset.portraitH };
}

/** Just enough of a Project for size questions, so this module never has to
 *  import the store (and create a cycle). */
export interface HasFrame {
  device: DeviceId;
  orientation: Orientation;
  /** Exact authored frame in CSS points. Wins over the preset when present. */
  canvas?: { w: number; h: number };
  /** Strips measured on the playing device. Wins over the table when present. */
  reserve?: { top: number; bottom: number };
}

/**
 * THE authoritative size of a project's canvas.
 *
 * A preset is a convenience, not a truth: "iPad" covers at least two different
 * shapes, and what a device actually hands a standalone PWA is not reliably
 * either of them. An iPad 8th gen measured 1068x800 of usable box against a
 * nominal 1080x810, so a preset-sized shell scaled to 0.989 and centred,
 * leaving a visible gap on every edge that no styling could close - the shell
 * cannot reach an edge it does not extend to.
 *
 * So a project may carry its own exact `canvas`, set from the real measured
 * viewport, and that always wins. `device`/`orientation` stay for labelling
 * and for going back to a preset deliberately.
 *
 * Additive and optional, exactly like `modifiedAt` before it: `projectSync`
 * round-trips whole objects through `JSON.stringify`, so an older build simply
 * carries the field through untouched rather than dropping it.
 */
export function canvasSizeOf(p: HasFrame): { w: number; h: number } {
  const c = p.canvas;
  if (c && c.w > 0 && c.h > 0) return { w: c.w, h: c.h };
  return canvasSize(p.device, p.orientation);
}

/**
 * The preset that wastes least of THIS screen, plus the orientation the screen
 * is actually being held in.
 *
 * Exists because choosing a frame by name is the wrong question to ask a user.
 * The operator's iPad 8th gen letterboxed for months against a preset called
 * simply "iPad", and the control to change it is a small chip on a project
 * card, four taps in - so the fix was neither discoverable nor obviously
 * needed. This lets the app offer the right answer where the problem is
 * visible instead.
 *
 * `min(1, ...)` mirrors the shell's own scale-to-fit, which never enlarges
 * past 1. Without that cap a phone preset scores brilliantly on a tablet by
 * pretending it would be blown up to fit.
 */
export function bestFitDevice(vw: number, vh: number): { device: DeviceId; orientation: Orientation; waste: number } | null {
  if (!(vw > 0 && vh > 0)) return null;
  const orientation: Orientation = vw >= vh ? 'landscape' : 'portrait';
  let best: { device: DeviceId; orientation: Orientation; waste: number } | null = null;
  for (const d of DEVICES) {
    const { w, h } = canvasSize(d.id, orientation);
    const scale = Math.min(1, vw / w, vh / h);
    const waste = 1 - (w * scale * h * scale) / (vw * vh);
    if (!best || waste < best.waste) best = { device: d.id, orientation, waste };
  }
  return best;
}

/**
 * The strips the installed app gives up to iOS on each device, in CSS points:
 * the status bar above the header, the home indicator below the canvas.
 *
 * WHY THIS IS A TABLE AND NOT JUST `env()`. On the device, design-tokens 12b
 * computes these from `env(safe-area-inset-*)` plus a 24pt status-bar floor,
 * and that only fires in `display-mode: standalone`. Everywhere else - the
 * desktop the operator authors on - they computed to 0, so the desktop canvas
 * was 24pt TALLER than the iPad's. MEASURED 2026-10-05 on the operator's own
 * frame (1080x810, ipad-10-2): canvas 772 tall on desktop, 748 on the iPad,
 * and their Page 1 button sat 8pt off the iPad's bottom edge while looking
 * fine on the desktop. Every control dragged to the bottom on the desktop lost
 * 24pt on stage. The shell applies the table everywhere (App.tsx), `max()`ed
 * with the live `env()` value, so the desktop previews the device's strips and
 * the device itself computes exactly what it did before.
 *
 * THE TABLE IS ONLY THE FALLBACK. A project played on its device records
 * what that device really measured (`Project.reserve`, `reserveOf` below),
 * because this table was wrong within two weeks of being written: the 24 was
 * taken off a September screenshot when iOS reported a 0 top inset, and on
 * iPadOS 26.6.1 the same iPad reports 32 (MEASURED 2026-10-05 by Navigate >
 * Details' frame report, `__frame_report_ipad`). The desktop canvas was then
 * 8pt taller than the iPad's, and a control dragged to the bottom was cut off.
 *
 * Values are what the installed app ends up with on that device, i.e.
 * top = max(env top, STATUS_BAR_FLOOR) and bottom = env bottom:
 *   - iPad 10.2-inch: 32 top (measured, iPadOS 26.6.1); home BUTTON, no home
 *     indicator, so bottom 0 (measured 0).
 *   - iPad Air / Pro 11-inch: home indicator, 20 in both orientations. The
 *     24 top is NOT measured, and iPadOS 26 may well report 32 here too.
 *   - iPhone 12 Pro Max: Apple's published insets (47 / 34 portrait, 0 / 21
 *     landscape, top floored to 24). Not measured on a device.
 * A new preset MUST add its row: a missing one previews no strips and
 * reintroduces exactly this bug for that device.
 */
export interface DeviceReserve { top: number; bottom: number }
const RESERVES: Record<DeviceId, Record<Orientation, DeviceReserve>> = {
  'ipad':              { landscape: { top: 24, bottom: 20 }, portrait: { top: 24, bottom: 20 } },
  'ipad-10-2':         { landscape: { top: 32, bottom: 0 },  portrait: { top: 32, bottom: 0 } },
  'iphone-12-pro-max': { landscape: { top: 24, bottom: 21 }, portrait: { top: 47, bottom: 34 } },
};
export function deviceReserve(device: DeviceId, orientation: Orientation): DeviceReserve {
  return RESERVES[device]?.[orientation] ?? { top: 0, bottom: 0 };
}

/** The installed app's status-bar floor: design-tokens 12b's
 *  `max(env(safe-area-inset-top), 24px)` in standalone. Mirrored here so the
 *  recorded reserve is what the shell applies; change both together. */
export const STATUS_BAR_FLOOR = 24;

/** THE strips for a project, like `canvasSizeOf` is THE frame: what its
 *  playing device measured, else the table. */
export function reserveOf(p: HasFrame): DeviceReserve {
  const r = p.reserve;
  if (r && r.top >= 0 && r.bottom >= 0) return { top: r.top, bottom: r.bottom };
  return deviceReserve(p.device, p.orientation);
}

export function deviceLabel(device: DeviceId, orientation: Orientation): string {
  const preset = DEVICES.find((d) => d.id === device);
  const name = preset?.label ?? device;
  return `${name} - ${orientation}`;
}

/** The shell's scale-to-fit factor for a canvas of `size` inside a viewport
 *  of `avail`. ONE implementation on purpose: App.tsx applies it and the
 *  new-project form predicts it, and if those two ever disagreed the form
 *  would promise a size the app does not deliver.
 *
 *  Clamped at 1 - the canvas is never blown up past its authored size, so a
 *  layout designed for an iPad does not turn into a blurry poster on a
 *  desktop monitor. */
export function fitScale(
  size: { w: number; h: number },
  avail: { w: number; h: number },
  /** The largest scale allowed. 1 everywhere except a desktop with Navigate
   *  docked (DEC-268: "use full window"), which passes Infinity. */
  cap = 1,
): number {
  if (size.w <= 0 || size.h <= 0 || avail.w <= 0 || avail.h <= 0) return 1;
  return Math.min(cap, avail.w / size.w, avail.h / size.h);
}

/** Below this, the UI is small enough that the operator cannot comfortably
 *  read the chrome - which is the state that locked them out on 2026-07-31.
 *  Surfaces at or under this threshold get the unscaled escape bar. */
export const READABLE_SCALE_FLOOR = 0.8;
